import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  GlobalSignOutCommand,
  GetUserCommand,
  ForgotPasswordCommand,
  ChangePasswordCommand,
} from "@aws-sdk/client-cognito-identity-provider";

const API_URL   = process.env.REACT_APP_API_URL;
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
const REGION    = process.env.REACT_APP_AWS_REGION || "us-east-2";

const cognitoClient = new CognitoIdentityProviderClient({ region: REGION });

const TOKEN_KEY = "psychxai_tokens";
function saveTokens(t) { try { localStorage.setItem(TOKEN_KEY, JSON.stringify(t)); } catch {} }
function getTokens() { try { return JSON.parse(localStorage.getItem(TOKEN_KEY) || "null"); } catch { return null; } }
function clearTokens() { try { localStorage.removeItem(TOKEN_KEY); } catch {} }
function getAccessToken() { return getTokens()?.AccessToken || null; }

const listeners = new Set();
function notifyListeners(event, session) { listeners.forEach(fn => fn(event, session)); }

async function apiCall(path, method = "GET", body = null) {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json();
  if (!res.ok) return { data: null, error: data };
  return { data, error: null };
}

export const auth = {
  async getSession() {
    const tokens = getTokens();
    if (!tokens?.AccessToken) return { data: { session: null } };
    try {
      const res = await cognitoClient.send(new GetUserCommand({ AccessToken: tokens.AccessToken }));
      const email = res.UserAttributes.find(a => a.Name === "email")?.Value;
      const role  = res.UserAttributes.find(a => a.Name === "custom:role")?.Value;
      return { data: { session: { access_token: tokens.AccessToken, user: { email, role } } } };
    } catch {
      clearTokens();
      return { data: { session: null } };
    }
  },

  onAuthStateChange(callback) {
    listeners.add(callback);
    auth.getSession().then(({ data: { session } }) => {
      callback(session ? "SIGNED_IN" : "SIGNED_OUT", session);
    });
    return { data: { subscription: { unsubscribe: () => listeners.delete(callback) } } };
  },

async signInWithPassword({ email, password }) {
    try {
      const res = await cognitoClient.send(new InitiateAuthCommand({
        AuthFlow:       "USER_PASSWORD_AUTH",
        ClientId:       CLIENT_ID,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      }));
      const tokens = res.AuthenticationResult;
      saveTokens(tokens);
      const session = { access_token: tokens.AccessToken, user: { email } };
      notifyListeners("SIGNED_IN", session);
      return { data: { session }, error: null };
    } catch (err) {
      console.error("Sign in error:", err);
      return { data: null, error: { message: err.message || "Sign in failed" } };
    }
  },

  async signOut() {
    const tokens = getTokens();
    if (tokens?.AccessToken) {
      try { await cognitoClient.send(new GlobalSignOutCommand({ AccessToken: tokens.AccessToken })); } catch {}
    }
    clearTokens();
    notifyListeners("SIGNED_OUT", null);
    return { error: null };
  },

  async resetPasswordForEmail(email) {
    try {
      await cognitoClient.send(new ForgotPasswordCommand({ ClientId: CLIENT_ID, Username: email }));
      return { error: null };
    } catch (err) {
      return { error: { message: err.message } };
    }
  },

  async updateUser({ password }) {
    const tokens = getTokens();
    if (!tokens?.AccessToken) return { error: { message: "Not signed in" } };
    try {
      await cognitoClient.send(new ChangePasswordCommand({
        AccessToken:      tokens.AccessToken,
        PreviousPassword: "",
        ProposedPassword: password,
      }));
      return { error: null };
    } catch (err) {
      return { error: { message: err.message } };
    }
  },
};

export const db = {
  clinics: {
    async getByEmail(email) { return apiCall(`/clinics?email=${encodeURIComponent(email)}`); },
    async getById(id) { return apiCall(`/clinics/${id}`); },
    async update(id, fields) { return apiCall(`/clinics/${id}`, "PATCH", fields); },
  },
  intakes: {
    async getByClinic(clinicId) { return apiCall(`/intakes?clinic_id=${clinicId}`); },
    async update(id, fields) { return apiCall(`/intakes/${id}`, "PATCH", fields); },
  },
};

export async function adminCall(action, payload = {}) {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/admin/clinic`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
    body:    JSON.stringify({ action, ...payload }),
  });
  return res.json();
}